import { useQueries } from '@tanstack/react-query';
import { getScenarioForecast } from '@/api/services/scenarioService';
import type { ScenarioForecastResponseApi } from '@/api/models/scenario';
import { useApp } from '@/contexts/AppContext';

interface UseScenarioForecastsResult {
  data: Record<string, ScenarioForecastResponseApi>;
  isLoading: boolean;
  isError: boolean;
}

export function useScenarioForecasts(
  scenarioIds: string[],
  period: string,
): UseScenarioForecastsResult {
  const { mode } = useApp();
  const isLive = mode === 'live';

  const numericIds = scenarioIds
    .map((id) => ({ original: id, numeric: Number(id) }))
    .filter((entry) => !isNaN(entry.numeric));

  const results = useQueries({
    queries: numericIds.map((entry) => ({
      queryKey: ['scenario-forecast', entry.numeric, period],
      queryFn: () => getScenarioForecast(entry.numeric, period),
      enabled: isLive,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const data: Record<string, ScenarioForecastResponseApi> = {};
  for (let i = 0; i < numericIds.length; i++) {
    const result = results[i];
    if (result.data) {
      data[numericIds[i].original] = result.data;
    }
  }

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);

  return { data, isLoading, isError };
}
